import React from 'react';

import {PageHeadline} from 'src/components/page-headline/page-headline';
import {Separator} from 'src/components/separator';
import {
    ChangeRequestSubscriptionDropdown,
} from 'src/modules/change-requests/subscription/change-request-subscription.dropdown';
import {CommentsController} from 'src/modules/comments/comments.controller';
import {MAPPED_CHANGE_REQUEST_STATUS} from 'src/modules/constants';
import {CopyLinkButton} from 'src/modules/event-review/copy-link-button/copy-link-button';
import {
    ChangeRequestMetadata,
} from 'src/modules/event-review/header-block/change-request-metadata/change-request-metadata';
import {Deprecated} from 'src/modules/event-review/header-block/deprecated/deprecated';
import {EventCode} from 'src/modules/event-review/header-block/event-code/event-code';
import {EventDescription} from 'src/modules/event-review/header-block/event-description/event-description';
import {EventSource} from 'src/modules/event-review/header-block/event-source/event-source';
import {EventTrigger} from 'src/modules/event-review/header-block/event-trigger/event-trigger';
import {
    EventTriggerAttachmentController,
} from 'src/modules/event-review/header-block/event-trigger-attachment/event-trigger-attachment.controller';
import {EventType} from 'src/modules/event-review/header-block/event-type/event-type';
import {Families} from 'src/modules/event-review/header-block/families/families';
import {MixpanelDecorated} from 'src/modules/event-review/header-block/mixpanel-decorated/mixpanel-decorated';
import {Reported} from 'src/modules/event-review/header-block/reported/reported';
import {useHighlightsHeaderInfo} from 'src/modules/event-review/hooks/useHighlightsHeaderInfo';
import {Attributes} from 'src/modules/event-review/views/attributes/attributes';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {EventBasicInfo} from 'src/openapi/models/EventBasicInfo';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';

type Props = {
    changeRequest: ChangeRequest,
    changeRequestId: number | null,
    existingEvent: EventBasicInfo | null,
    eventCodeInfo: EventDetailInfo | null,
    onStartReview: () => void,
    onStopReview: () => void,
    onSendToApprove: () => void,
    onApprove: () => void,
    onReopen: (openUpdateForm: boolean) => void,
    onReject: () => void,
    onShowUserFeedbackForm: () => void,
    seeEventHandler: () => void,
};

export const EventReviewView = (props: Props) => {
    const {
        changeRequest,
        changeRequestId,
        existingEvent,
        eventCodeInfo,
        onStartReview,
        onStopReview,
        onSendToApprove,
        onApprove,
        onReopen,
        onReject,
        onShowUserFeedbackForm,
        seeEventHandler,
    } = props;

    const highlightsInfo = useHighlightsHeaderInfo(changeRequest, eventCodeInfo);

    return (
        <>
            <PageHeadline
                caption={`${changeRequest.status !== ChangeRequestStatus.WAITING_REVIEW ?
                    'Change Request' : 'Event Review'} (action: ${
                    !changeRequest.event?.id ? 'New Event' : 'Update Event'
                }, status: ${MAPPED_CHANGE_REQUEST_STATUS[changeRequest.status]})`}>
                <Group>
                    <CopyLinkButton changeRequest={changeRequest}/>
                    {existingEvent ? (
                        <Button onClick={seeEventHandler} variant="Type.SECONDARY">
                            View Event
                        </Button>
                    ) : null}
                    <ChangeRequestSubscriptionDropdown changeRequestId={changeRequestId}/>
                </Group>
            </PageHeadline>
            <Group direction="v" gap="xl">
                <Item style={{width: '100%'}}>
                    <Group justify="space-between" gap="xs" alignItems="start">
                        <Item style={{width: '50%'}}>
                            <Group direction="v">
                                <Group style={{width: '100%'}} className="pr-m">
                                    <Group direction="v" style={{width: '50%'}}>
                                        <EventType changeRequest={changeRequest}/>
                                        <EventCode changeRequest={changeRequest}/>
                                    </Group>
                                    <Group direction="v" style={{width: '50%'}}>
                                        <EventSource changeRequest={changeRequest}/>
                                        <Families
                                            changeRequest={changeRequest}
                                            eventCodeInfo={eventCodeInfo}
                                            highlightsInfo={highlightsInfo}
                                        />
                                    </Group>
                                </Group>
                                <EventDescription
                                    changeRequest={changeRequest}
                                    eventCodeInfo={eventCodeInfo}
                                    highlightsInfo={highlightsInfo}
                                />
                                <EventTrigger
                                    changeRequest={changeRequest}
                                    eventCodeInfo={eventCodeInfo}
                                    highlightsInfo={highlightsInfo}
                                />
                                <Group gap="l">
                                    <Reported
                                        changeRequest={changeRequest}
                                        eventCodeInfo={eventCodeInfo}
                                        highlightsInfo={highlightsInfo}
                                    />
                                    <MixpanelDecorated
                                        changeRequest={changeRequest}
                                        eventCodeInfo={eventCodeInfo}
                                        highlightsInfo={highlightsInfo}
                                    />
                                    <Deprecated
                                        changeRequest={changeRequest}
                                        eventCodeInfo={eventCodeInfo}
                                        highlightsInfo={highlightsInfo}
                                    />
                                </Group>
                                <Item style={{width: '100%'}}>
                                    <ChangeRequestMetadata changeRequest={changeRequest}/>
                                </Item>
                            </Group>
                        </Item>
                        <Group direction="v" style={{width: '45%'}}>
                            <EventTriggerAttachmentController
                                changeRequest={changeRequest}
                                eventCodeInfo={eventCodeInfo}
                            />
                            <Separator/>
                            <CommentsController
                                changeRequest={changeRequest}
                                changeRequestId={changeRequestId}
                            />
                        </Group>
                    </Group>
                </Item>
                <Item>
                    <Attributes
                        changeRequest={changeRequest}
                        eventCodeInfo={eventCodeInfo}
                        changeRequestId={changeRequestId}
                        onStartReview={onStartReview}
                        onStopReview={onStopReview}
                        onSendToApprove={onSendToApprove}
                        onApprove={onApprove}
                        onReopen={onReopen}
                        onReject={onReject}
                        onShowUserFeedbackForm={onShowUserFeedbackForm}
                    />
                </Item>
            </Group>
        </>
    );
};
